import {Component, OnInit, ViewChild, HostListener, Inject, PLATFORM_ID, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {ProductsService} from '../shared/services/products.service';
import {Project} from '../shared/models/project';
import {ProjectService} from '../shared/services/project.service';
import {MatDialog} from '@angular/material/dialog';
import {FiltersComponent} from './filters/filters.component';
import {CartService} from '../shared/services/cart.service';
import {WaitModalComponent} from '../wait-modal/wait-modal.component';
import {MatSelectorComponent} from './product-selectors/mat-selector/mat-selector.component';
import {ComponentCanDeactivate} from './pending-changes.guard';
import {Subscription} from 'rxjs';
import {environment} from '../../environments/environment';
import {YesNoDialogComponent} from '../yesno-dialog/yesno-dialog.component';
import {CarouselComponent} from '../shared/components/carousel/carousel.component';
import {ActivatedRoute, Router} from '@angular/router';
import {SelectorDialogComponent} from './selector-dialog/selector-dialog.component';
import {Size} from '../shared/models/size';
import {OpeningSizeCalculatorDialogComponent} from './opening-size-calculator-dialog/opening-size-calculator-dialog.component';
import {SizePipe} from '../shared/pipes/size.pipe';
import {InchesPipe} from '../shared/pipes/inches.pipe';
import {Opening} from '../shared/models/opening';
import {CookieService} from '../cookie.service';
import {SeoService} from '../shared/services/seo.service';
import {WINDOW} from '../shared/services/window.service';
import {Shape} from '../shared/models/shape';
import {CurrencyPipe, isPlatformBrowser} from '@angular/common';
import {ProjectPreviewComponent} from './project-preview/project-preview.component';
import {Design} from '../shared/models/design';
import {SessionService} from '../shared/services/session.service';
import {ChatService} from '../shared/services/chat.service';
import {ContactUsDialogComponent} from '../contact-us-dialog/contact-us-dialog.component';
import {CmsService} from "../shared/services/cms.service";
import {BreakpointObserver} from "@angular/cdk/layout";
import {BREAKPOINTS} from "../shared/constants/breakpoints";
import {PartDisplayService} from "../shared/services/part-display.service";

@Component({
    selector: 'mbnm-builder',
    templateUrl: './builder.component.html',
    styleUrls: ['./builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuilderComponent implements OnInit, OnDestroy, ComponentCanDeactivate  {

  constructor(private products: ProductsService, private projectService: ProjectService, private cartService: CartService,
              public media: BreakpointObserver, public dialog: MatDialog, private route: ActivatedRoute, private router: Router,
              private cookieService: CookieService, public seoService: SeoService, @Inject(WINDOW) private window: Window,
              @Inject(PLATFORM_ID) private platformId: Object, private chatService: ChatService,
              private cmsService: CmsService, public partDisplay: PartDisplayService) {

  }

  public get builderSubHeading(): string {
    if (!this.project) return '';

    let product = '';
    if (this.project.primaryProduct === 'MAT') {
      product = this.partDisplay.description(this.project.topMat) || '';
      if (this.project.bottomMat) product += ` on ${this.partDisplay.description(this.project.bottomMat)}`;
      if (this.project.moulding) product += ` with ${this.partDisplay.description(this.project.moulding)}`;
    } else if (this.project.primaryProduct === 'FRAME') {
      product = this.partDisplay.description(this.project.moulding) || '';
      if (this.project.topMat) product += ` with ${this.partDisplay.description(this.project.topMat)}`;
      if (this.project.bottomMat) product += ` on ${this.partDisplay.description(this.project.bottomMat)}`;
    }
    return product || '';
  }

  private disableLevelMessaging:boolean = false;


  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  async canDeactivate() {
    await this.cartService.getCart();
    return environment.disablePreventNavigation === true || !this.project || this.project.isEmpty || this.editCancelled || this.disableLevelMessaging;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (!this.canDeactivate()) {
      $event.returnValue = 'Leaving so soon? Don\'t worry, your order will still be here when you come back.';
    }
  }
  projectThumbs: any;
  projectThumbsImageCounter: number = 1;
  selectedCarouselTab: number = 0;
  shareBaseUrl: string = environment.baseUrls().site + '/buy/project/';
  tileData: Array<any> = [];
  chatStatus = 'NOT_INITIALIZED';
  getHelpWithDesignDeferredData = null;
  filterChoices: any = {
    quality: [
      {
        value: 'Economy',
        icon: 'star-outline',
        caption: 'Economy',
        largeIcon: true
      },
      {
        value: 'Standard',
        icon: 'star-half',
        caption: 'Standard',
        largeIcon: true
      },
      {
        value: 'Conservation',
        icon: 'star',
        caption: 'Conservation<div>ACID FREE</div>',
        largeIcon: true,
      }
    ],
    core: [
      {
        value: 'Cream',
        swatch: '#f4eade',
        caption: 'Cream'
      },
      {
        value: 'White',
        swatch: '#ffffff',
        caption: 'White<div>ACID FREE</div>'
      },
      {
        value: 'Black',
        swatch: '#2f2f2f',
        caption: 'Black<div>ACID FREE</div>'
      }
    ],
    thickness: [
      {
        value: '4 Ply',
        label: {
          class: 'ply',
          text: '4'
        }
      },
      {
        value: '8 Ply',
        label: {
          class: 'ply',
          text: '8'
        }
      }
    ],
    finish: [
      {
        value: 'Paper',
        swatch: '#f4eade',
        caption: 'Paper'
      },
      {
        value: 'Suede',
        imageSwatch: '/assets/images/products/mats/suede.jpg',
        caption: 'Suede'
      }
    ]
  };

  productCategories: any = {
    topMat: {
      projectProperty: 'topMat',
      type: 'MAT',
      filterOptions: [
        {
          sort: 2,
          title: 'Quality',
          tagName: 'quality',
          descriptiveStringFormat: '{value} Quality',
          selection: 'Standard',
          defaultSelection: 'Standard',
          choices: this.filterChoices.quality,
          helpInfo: {
            title: 'What Quality Do I Need?',
            message: '<p>The quality you need depends on what you are framing/matting.</p>' +
            '<p><strong>Ecnomony</strong> mats come in lots of colors and are similar to the quality found in most retail stores.</p>' +
            '<p><strong>Standard</strong> mats are made to last and are slightly thicker than economy mats. Perfect for prints and most art work.</p>' +
            '<p><strong>Conservation</strong> mats are acid free, making them perfect for fine art prints. In addition to 4 ply, they also come in an extra thick 8 ply version.</p>'
          }
        },
        {
          sort: 3,
          title: 'Core Color',
          tagName: 'coreType',
          descriptiveStringFormat: '{value} Core',
          selection: 'White',
          defaultSelection: 'White',
          choices: this.filterChoices.core,
          helpInfo: {
            title: 'Core Color',
            message: '<p>A mat\'s "core" refers to the edge you can see where the mat is cut.' +
            '<p class="image"><img src="/assets/images/help/mat-core.png" alt="Matboard Cores" /></p>' +
            '<p>We offer mats with <strong>Black</strong>, <strong>White</strong> and <strong>Cream</strong> cores.</p>'
          }
        },
        {
          sort: 4,
          title: 'Thickness',
          tagName: 'thickness',
          descriptiveStringFormat: '{value}',
          selection: '4 Ply',
          defaultSelection: '4 Ply',
          choices: this.filterChoices.thickness,
          helpInfo: {
            title: 'Mat Thickness',
            message: '<p>Similar to other paper product, matboard thickness is measured in the number of plys.</p>' +
                     '<p>We offer 4 & 8 ply mats. Typically, the thicker the mat, the higher quality it is.</p>'
          }
        },
        {
          sort: 5,
          title: 'Finish',
          tagName: 'finish',
          descriptiveStringFormat: '{value}',
          selection: 'Paper',
          defaultSelection: 'Paper',
          choices: this.filterChoices.finish,
          helpInfo: {
            title: 'What Finishes Do You Offer?',
            message: '<p>The finish of the mat is the visible front surface.</p>' +
              '<p><strong>Paper</strong> finish mats are our most popular, with hundreds of color choices and acid free options.</p>' +
              '<p><strong>Suede</strong> finish mats offer a luxurious, suede finish that are velvety to the touch.</p>' +
              '<p><strong>Linen</strong> finish mats are fabric mats, with texture and patterns for a luxurious look.</p>'
          }
        }
      ],
      thumbPrefix: '/assets/images/products/mats/',
      thumbPostfix: '.jpg',
      title: 'Primary Mat Color',
      helpInfo: {
        title: 'Matboards',
        message: '<p>Adding a primary mat to your frame purchase can enhance the over all look and give you that pop of color you need.</p>' +
        '<p>We recommend getting foam backing & plexiglass with your frame.</p>'
      },
      cols: 4,
      scrollToTopAfterSelection: true,
      layers: [{title: 'Primary Mat', projectProperty: 'topMat'}],
      selection: [],
      showSamplesButtonInSelector: true
    },
    bottomMat: {
      projectProperty: 'bottomMat',
      type: 'MAT',
      dependencies: ['bottomMatAllowed', 'topMat'],
      missingDependencyMessage: ['This is a Single Mat Only Project', 'Select Primary Mat to Enable'],
      filterOptions: [
        {
          sort: 1,
          title: 'Quality',
          tagName: 'quality',
          descriptiveStringFormat: '{value} Quality',
          selection: 'Standard',
          defaultSelection: 'Standard',
          choices: this.filterChoices.quality,
          helpInfo: {
            title: 'What Quality Do I Need?',
            message: '<p>The quality you need depends on what you are framing/matting.</p>' +
            '<p><strong>Ecnomony</strong> mats come in lots of colors and are similar to the quality found in most retail stores.</p>' +
            '<p><strong>Standard</strong> mats are made to last and are slightly thicker than economy mats. Perfect for prints and most art work.</p>' +
            '<p><strong>Conservation</strong> mats are acid free, making them perfect for fine art prints. In addition to 4 ply, they also come in an extra thick 8 ply version.</p>'
          }
        },
        {
          sort: 2,
          title: 'Core Color',
          tagName: 'coreType',
          descriptiveStringFormat: '{value} Core',
          selection: 'White',
          defaultSelection: 'White',
          choices: this.filterChoices.core,
          helpInfo: {
            title: 'Core Color',
            message: '<p>A mat\'s "core" refers to the edge you can see where the mat is cut.' +
            '<p class="image"><img src="/assets/images/help/mat-core.png" alt="Matboard Cores" /></p>' +
            '<p>We offer mats with <strong>Black</strong>, <strong>White</strong> and <strong>Cream</strong> cores.</p>'
          }
        },
        {
          sort: 3,
          title: 'Thickness',
          tagName: 'thickness',
          descriptiveStringFormat: '{value}',
          selection: '4 Ply',
          defaultSelection: '4 Ply',
          choices: this.filterChoices.thickness,
          helpInfo: {
            title: 'Mat Thickness',
            message: '<p>Similar to other paper product, matboard thickness is measured in the number of plys.</p>' +
                     '<p>We offer 4 & 8 ply mats. Typically, the thicker the mat, the higher quality it is.</p>'
          }
        },
        {
          sort: 4,
          title: 'Finish',
          tagName: 'finish',
          descriptiveStringFormat: '{value}',
          selection: 'Paper',
          defaultSelection: 'Paper',
          choices: this.filterChoices.finish,
          helpInfo: {
            title: 'Finish & Materials',
            message: '<p>Finish description.</p>'
          }
        }
      ],
      thumbPrefix: '/assets/images/products/mats/',
      thumbPostfix: '.jpg',
      title: 'Accent Mat Color',
      upsellText: 'Considering the Accent Mat Option?',
      isRequiredText: 'Please select the Required Accent Mat.',
      helpInfo: {
        title: 'Matboards',
        message: '<p>An accent or bottom mat is an optional second layer of matting that goes below your primary layer.</p>' +
        '<p>Typically 1/4" of the accent layer shows.  This is called the reveal.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/accent-mat.jpg" alt="Matboard Accent Layer" /></p>'
      },
      cols: 4,
      scrollToTopAfterSelection: true,
      layers: [{title: 'Accent Mat', projectProperty: 'bottomMat', projectDependencies: ['topMat', 'opening', 'bottomMatAllowed']}],
      selection: [],
      showSamplesButtonInSelector: true
    },
    moulding: {
      projectProperty: 'moulding',
      type: 'FRAME',
      title: 'Picture Frame',
      cols: 4,
      mobileCols: 2,
      choice: null,
      choices: [
        {
          icon: 'check',
          color: 'green',
          value: true
        },
        {
          icon: 'x',
          color: 'red',
          value: false
        }
      ],
      products: [],
      thumbPrefix: '/assets/images/products/frames/',
      thumbPostfix: '/thumb.jpg',
      groupThumbPostfix: '/front-thumb.jpg',
      helpInfo: {
        title: 'Picture Frame Options',
        message: '<p>We offer custom cut frames in a variety of finishes & materials.  Our frames come ready to hang with saw tooth hangers.</p>' +
        '<div class="image labels"><img src="/assets/images/help/frame-anatomy.jpg" alt="Anatomy of a Matboard"/><span>Backing Board</span>' +
        '<span>Artwork</span><span>Accent Mat</span><span>Primary Mat</span><span>Plexiglass</span>' +
        '<span class="hl">Picture Frame</span></div>' +
        '<p>We recommend getting foam backing & plexiglass with your frame.</p>'
      },
      selection: [],
      showSamplesButtonInSelector: true
    },
    backingBoard: {
      projectProperty: 'backingBoard',
      title: 'Backing Board',
      type: 'BACKINGBOARD',
      simpleSize: true,
      cols: 3,
      mobileCols: 2,
      choice: null,
      choices: [
        {
          icon: 'check',
          color: 'green',
          value: true
        },
        {
          icon: 'x',
          color: 'red',
          value: false
        }
      ],
      products: [],
      defaultProductCode: 'FOAM',
      thumbPrefix: '/assets/images/products/backingboards/',
      thumbPostfix: '.jpg',
      helpInfo: {
        title: 'Backing Boards',
        message: '<p>Backing boards are used to protect the back of your print, they are separate uncut pieces.</p>' +
        '<p>We custom cut our backing boards to match the outer size of your matboard.</p>' +
        '<div class="image labels"><img src="/assets/images/help/frame-anatomy.jpg" alt="Anatomy of a Matboard" /><span class="hl">Backing Board</span>' +
        '<span>Artwork</span><span>Accent Mat</span><span>Primary Mat</span><span>Plexiglass</span><span>Picture Frame</span></div>'
      },
      scrollToTopAfterSelection: false,
      selection: [],
      showSamplesButtonInSelector: true
    },
    glazing: {
      projectProperty: 'glazing',
      type: 'GLAZING',
      title: 'Plexiglass',
      cols: 1,
      mobileCols: 1,
      choice: null,
      choices: [
        {
          icon: 'check',
          color: 'green',
          value: true
        },
        {
          icon: 'x',
          color: 'red',
          value: false
        }
      ],
      products: [],
      defaultProductCode: 'PLEXIGLASSPREMIUM',
      thickerDefaultProductCode: 'PLEXIGLASSPREMIUM',
      thumbPrefix: '/assets/images/products/glazings/',
      thumbPostfix: '.jpg',
      helpInfo: {
        title: 'Plexiglass Glazing',
        message: '<p>Plexiglass is a great alternative for glass that is durable and clear.</p>' +
        '<p>We custom cut our plexiglass to fit your matboard outer size and to fit snugly with our frames.</p>' +
        '<div class="image labels"><img src="/assets/images/help/frame-anatomy.jpg" alt="Anatomy of a Matboard" /><span>Backing Board</span>' +
        '<span>Artwork</span><span>Accent Mat</span><span>Primary Mat</span>' +
        '<span class="hl">Plexiglass</span><span>Picture Frame</span></div>'
      },
      scrollToTopAfterSelection: false,
      selection: [],
      showSamplesButtonInSelector: true
    },
    showBag: {
      noProductsAvailableMessage: 'There are no show bags matching your artwork size available.  If you have not entered a size yet, please do so before choosing show bags.',
      projectProperty: 'bag',
      type: 'SHOWBAG',
      cols: 1,
      mobileCols: 1,
      preample: 'Our show bags are made to fit the artwork and/or mat sizes listed below.  Please choose the size you need to continue.',
      thumbPrefix: '/assets/images/products/bags/',
      thumbPostfix: '.jpg',
      title: 'Show Bags',
      choice: null,
      choices: [
        {
          icon: 'check',
          color: 'green',
          value: true
        },
        {
          icon: 'x',
          color: 'red',
          value: false
        }
      ],
      products: [],
      helpInfo: {
        title: 'Show Bags',
        message: '<p>Show bags are used to protect your artwork. They come with a protective flap and are perfect for show kits (backing, matboard and show bag).</p><p>They are available for the following sizes:</p><p>5x7", 8x10", 9x12", 11x14", 12x12", 12x16", 12x18", 14x18" 16x20", 18x24", 20x24", 22x28" & 20x30"</p>'
      },
      scrollToTopAfterSelection: false,
      selection: [],
      showSamplesButtonInSelector: true
    },
    samplePack: {
      type: 'SAMPLE_PACK',
      title: 'Sample Packs',
      cols: 3,
      mobileCols: 1,
      products: [],
      helpInfo: {
        title: 'Sample Packs',
        message: '<p>Sample packs are a great way to see all our colors so you can be sure you have picked the right mat for your project.</p>'
      },
      scrollToTopAfterSelection: false,
      selection: [],
      showSamplesButtonInSelector: false
    }
  };

  private sizeHelpInfo:any = {
    outer: {
      heading: 'Outer Size',
      helpInfo: {
        title: 'Choosing the Right Outer Size',
        message: '<p><strong><u>With</u> Frame</strong></p>' +
        '<p>Your outer size should match the frame size as measured <i>from the back</i> to take into account the frame rabbet & matches the plexiglass size.</p>' +
        '<p><strong><u>Without</u> Frame</strong></p>' +
        '<p>No specific limitations up to our max of 32" x 40".</p>'

      }
    },
    opening: {
      heading: 'Opening Size',
      helpInfo: {
        title: 'Opening Size',
        message: '<p>Your opening size should be slightly (1/4" on each side) smaller than your artwork size to ensure that your artwork is covered (no visible gaps).</p><p>All opening are beveled 45 degrees. Adding an accent mat does not impact your opening size.</p>',
        yesButtonText: 'Calculate Opening Size',
        yesClickedCallback: function () {
          const dialogRef = this.dialog.open(OpeningSizeCalculatorDialogComponent, {});
          dialogRef.componentInstance.project = this.project;
          dialogRef.afterClosed().subscribe((opening: Size) => {
            if (opening) {
              this.project.opening.size.width = opening.width;
              this.project.opening.size.height = opening.height;
            }
          });
        },
        yesClickedCallbackContext: this
      }
    },
    openingShape: {
      heading: 'Opening Shape',
      helpInfo: {
        title: 'Opening Shapes',
        message: '<p>Your opening can come in a number of shapes. Use the drop to choose a shape for your opening to see what it will look like.  Please note the shape of your opening also depends on the width and height.</p>'
      }
    },
    reveal: {
      heading: 'Reveal',
      helpInfo: {
        title: 'What is a Reveal?',
        message: '<p>This refers to how wide the accent mat is. The standard size is typically 1/4" but you can choose to increase that to 3/8" or 1/2" if desired.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/reveal.jpg" alt="Matboard Reveal"/></p>'
      }
    },
    bottomMargin: {
      heading: 'Bottom Margin',
      helpInfo: {
        title: 'What is a Bottom Margin?',
        message: '<p>This refers to the height of the matboard border. We center your artwork by default & set the bottom margin accordingly.</p><p>You can also choose to "top center" (equal side & top margins) your artwork, or set a custom margin.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/bottom-margin.jpg" alt="Matboard Accent Margin"/></p>'
      }
    },
    customize: {
      heading: 'Customize',
      helpInfo: {
        title: 'Customization',
        message: 'Customer Service Only!'
      }
    }
  }

  private sizeSelectorWithMats:any = {
    type: 'SIZE-SELECTOR',
    title: 'Outer Size & Opening',
    choice: null,
    helpInfo: {
      title: 'Outer Size & Opening'
    },
    selection: [],
    showSamplesButtonInSelector: false
  };

  private sizeSelectorWithoutMats:any = {
    type: 'SIZE-SELECTOR',
    title: 'Size',
    choice: null,
    helpInfo: {
      title: 'Size'
    },
    selection: [],
    showSamplesButtonInSelector: false
  };


  @ViewChild('filters')
  private _filters: FiltersComponent;

  /*
  COMMENTING OUT SINCE PRICES NOT BEING USED
  categories:any;
  setProductPrices() {
    let priceFn = (product) => {
      let matchingCategory = this.categories.filter( (category) => {
        return product.categoryId == category.categoryId;
      })[0];
      product.price = matchingCategory  ? matchingCategory.price : 0.00;
    };

    this.productCategories.moulding.products.forEach( priceFn );
    this.productCategories.backingBoard.products.forEach( priceFn );
    this.productCategories.glazing.products.forEach( priceFn );
    this.productCategories.showBag.products.forEach( priceFn );
  }
  */

  onOptionValueChange(newValue, option) {
    option.choice = newValue;
    if (!newValue) {
      option.selection = [];
    }
  }

  getProjectSamples(type:string) {
    if (this.project && this.project.numSamples > 0) {
      const samples = this.project.samples.filter( (sample) => {
        return sample.type === type;
      });

      if (type === 'MAT') {
        samples.sort((a, b) => {
          const categoryComparison = (a.categoryName || '').localeCompare(b.categoryName || '');
          return categoryComparison || this.partDisplay.description(a).localeCompare(this.partDisplay.description(b));
        });
      }

      return samples;
    }
  }

  isSampleCategoryStart(samples: Array<any>, index: number): boolean {
    return index === 0 || samples[index - 1].categoryName !== samples[index].categoryName;
  }

  onSelectedProductChange(newValues, option) {
    if (option) {
      if (this.builderType === 'SAMPLE') {
        this.project.clearSamples(option.type);
        option.selection = newValues;
        newValues.forEach( (newValue) => {
          newValue.type = option.type;
          if (newValue.type === 'SHOWBAG') {
            newValue = Object.assign({}, newValue);
            newValue.description = 'for ' + newValue.matSize;
            newValue.longDescription = 'Artwork or Mat';
          }
          this.project.addSample(newValue);
        });

        return;
      }

      const newValue = newValues.length > 0 ? newValues[0] : null;

      // if we removed backing/glazing but have a frame... give chance to bring it back
      if (this.project.moulding && (option.projectProperty === 'glazing' || option.projectProperty === 'backingBoard') && newValue == null) {
        const preservedValue = this.project[option.projectProperty];
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        const prod = (option.projectProperty === 'backingBoard' ? 'Backing Board' : 'Plexiglass');
        instance.data = {
          title: 'Keep ' + prod + '?',
          message: '<p>You have selected a picture frame. We highly recommend keeping your ' + prod.toLowerCase() + ' selection when ordering a frame.</p>',
          yesButtonText: 'Keep ' + prod,
          noButtonText: 'Remove'
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.project[option.projectProperty] = preservedValue;
            this.productCategories[option.projectProperty].selection = [preservedValue];
          } else {
            this.productCategories[option.projectProperty].disableAutomatedSelection = true;
          }
        });
      }

      // suggest conservation backing
      if (option.projectProperty === 'backingBoard' && newValue && newValue.quality !== 'CONSERVATION' && this.project.hasConservationMats) {
        const preservedValue = this.project[option.projectProperty];
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;

        if (preservedValue && preservedValue.quality === 'CONSERVATION') {
          instance.data = {
            title: 'Keep Archival Backing Boards?',
            message: '<p>You have selected acid-free mats but non-archival quality backing boards.  Consider keeping your previous archival backing board selection.</p>',
            yesButtonText: 'Keep Archival',
            noButtonText: 'No Thanks'
          };
        } else {
          instance.data = {
            title: 'Archival Backing Boards?',
            message: '<p>You have selected acid-free mats but non-archival quality backing boards.  Consider upgrading to an archival quality backing board.</p>',
            yesButtonText: 'Upgrade',
            noButtonText: 'No Thanks'
          };
        }

        dialogRef.afterClosed().subscribe((result:boolean) => {
          if (result) {
            // if we have a preserved, previous value find it
            if (preservedValue && preservedValue.quality === 'CONSERVATION') {
              this.productCategories.backingBoard.selection = this.productCategories.backingBoard.products.filter( (product) => {
                return product.code === preservedValue.code;
              });
            // otherwise pick any conservation backing
            } else {
              this.productCategories.backingBoard.selection = [this.productCategories.backingBoard.products.find( (product) => {
                return product.quality === 'CONSERVATION';
              })];
            }

            this.project.backingBoard = this.productCategories.backingBoard.selection[0];
          }
        });
      }

      option.selection = newValue ? [newValue]: [];
      option.choice = true;

      if (option.type === 'MAT' && this.project.primaryProduct !== 'MAT') {
        if (this.project.numOpenings === 0) {
          this.project.convertOuterSizeToOpening();
        } else if (!newValue && option.projectProperty === 'topMat') {
          this.project.convertOpeningSizeToOuter();
        }
      }

      this.setProjectProperty(option.projectProperty, newValue);

      // changes to a moulding
      if (option.projectProperty === 'moulding') {
        if (newValue) {
          if (!this.project.backingBoard) {
            if (!this.productCategories.backingBoard.disableAutomatedSelection) {
              this.productCategories.backingBoard.selection = this.productCategories.backingBoard.products.filter(product => {
                return product.code === this.productCategories.backingBoard.defaultProductCode;
              });
              this.productCategories.backingBoard.selection[0].automaticallySet = true;
              this.setProjectProperty('backingBoard', this.productCategories.backingBoard.selection[0]);
            }
          }

          if (!this.project.glazing) {
            if (!this.productCategories.glazing.disableAutomatedSelection) {
              this.productCategories.glazing.selection = this.productCategories.glazing.products.filter(product => {
                return product.code === this.productCategories.glazing.defaultProductCode;
              });
              this.productCategories.glazing.selection[0].automaticallySet = true;
              this.setProjectProperty('glazing', this.productCategories.glazing.selection[0], true);
            }
          }
        } else {
          if (this.project.glazing && this.project.glazing.automaticallySet) {
            this.project.glazing = null;
            this.productCategories.glazing.selection = [];
          }

          if (this.project.backingBoard && this.project.backingBoard.automaticallySet) {
            this.project.backingBoard = null;
            this.productCategories.backingBoard.selection = [];
          }
        }
      }

      option.wasAutoDeselected = false;
      if (option.scrollToTopAfterSelection && newValue) {
        this.scrollToTop();
      }
    }
  };

  productRemovedInSummary(propertyName) {
    this.removeProductSelection(null, this.productCategories[propertyName]);
  }

  removeProductSelection($event, option) {
    option.selection = [];
    option.choice = false;

    // if removing the topMat, make sure bottomMat is also removed from view
    if (option.projectProperty === 'topMat' && this.project.bottomMat) {
      this.removeProductSelection(null, this.productCategories.bottomMat);
    }

    if ($event) {
      $event.stopPropagation();
    }

    this.onSelectedProductChange([], option);
  }

  removeVGroove($event) {
    $event.stopPropagation();
    this.project.vGrooveOffset = 0;
  }

  project: Project;

  @ViewChild('mobileCarouselComponent') carouselComponent: CarouselComponent;

  ready:boolean = false;
  builderType: string;
  currentCartIsOrder: boolean = false;
  currentCart: any;
  errorLoadingProject: boolean = false;
  ssrProjectLoad: boolean = false;
  abTestSubRef: Subscription;
  cartChangeSub: Subscription;
  chatSubRef: Subscription;
  projectChangeSubRef: Subscription;
  projectSizeChangeSubRef: Subscription;
  projectSizeChange2SubRef: Subscription;

  correctBadFilters(filterOptions: Array<any>): void {
    filterOptions.forEach( filterOption => {
      const match = filterOption.choices.filter( choice => {
        return choice.value.toLowerCase() === filterOption.selection;
      });
      if (match.length === 0) {
        filterOption.selection = filterOption.defaultSelection;
      }
    });
  }

  private matProductsMatchingUrlParams(params, includeCore: boolean): Array<any> {
    return this.productCategories.topMat.products.filter((product) => {
      return (product.quality.trim().toLowerCase() == params['quality'] || typeof params['quality'] === 'undefined') &&
        (!includeCore || product.coreType.toLowerCase() === params['core'] || typeof params['core'] === 'undefined') &&
        (product.thickness.toLowerCase() === params['thickness'] || typeof params['thickness'] === 'undefined') &&
        (product.finish.toLowerCase() === params['finish'] || typeof params['finish'] === 'undefined');
    });
  }

  private redirectMatboardUrlWithoutCoreIfNecessary(params): boolean {
    if (params['primaryProduct'] !== 'MAT' || typeof params['core'] === 'undefined') return false;
    if (this.matProductsMatchingUrlParams(params, true).length > 0) return false;
    if (this.matProductsMatchingUrlParams(params, false).length === 0) return false;

    const canonicalUrl = this.router.url.replace(
      /^\/buy\/(matboards?\/[^_\/?#]+)(?:_[^\/?#]+)*(?=\/|$|\?)/i,
      '/buy/$1'
    );

    if (canonicalUrl === this.router.url) return false;

    this.router.navigateByUrl(canonicalUrl, { replaceUrl: true });
    return true;
  }

  public showSpinnerIfNecessary = false;
  async ngOnInit() {

    // if doesn't load fast enough, show a spinner (but avoid content shift on SSR by delaying)
    if (isPlatformBrowser(this.platformId)) {
      setTimeout( () => {
        this.showSpinnerIfNecessary = true;
      }, 1000);
    }

    // TODO: Figure out how to get route reuse strategy via providers to actually work?
    this.router.routeReuseStrategy.shouldReuseRoute = function() { return false; };

    this.cartChangeSub = CartService.currentCartChanged.subscribe( (currentCart) => {
      this.currentCartIsOrder = currentCart ? currentCart.orderConfirmationNumber : false;
      this.currentCart = currentCart;
    });

    const editMode = this.route.parent.snapshot.url[0].path === 'edit';
    const params = this.route.snapshot.params;
    const upsellProperty = params['upsellProperty'];
    const projectId = params['projectId'] || params['predefinedProductProjectId'];
    // ... or a partially completed one
    const tempProjectId = params['tempProjectId'];
    // ... or if we are starting a fresh one

    if (params['primaryProduct'] === 'MAT') {
      this.builderType = 'MAT';

      // set filter selections based on URL
      this.productCategories.topMat.filterOptions[0].selection = params['quality'];
      this.productCategories.topMat.filterOptions[1].selection = params['core'];
      this.productCategories.topMat.filterOptions[2].selection = params['thickness'];
      this.productCategories.topMat.filterOptions[3].selection = params['finish'];
      this.productCategories.bottomMat.filterOptions[0].selection = params['quality'];
      this.productCategories.bottomMat.filterOptions[1].selection = params['core'];
      this.productCategories.bottomMat.filterOptions[2].selection = params['thickness'];
      this.productCategories.bottomMat.filterOptions[3].selection = params['finish'];

      this.correctBadFilters(this.productCategories.topMat.filterOptions);
      this.correctBadFilters(this.productCategories.bottomMat.filterOptions);
    }
    else if (params['primaryProduct'] === 'FRAME') {
      this.builderType = 'FRAME';
      this.productCategories.moulding.filteredProductsGroupName = params['groupName'];
    }
    else if (params['primaryProduct'] === 'SAMPLES') {
      this.builderType = 'SAMPLE';
      this.productCategories.topMat.title = 'Matboards';
      this.productCategories.topMat.helpInfo.message =  '<p>Matboards can enhance the over all look of your artwork and give you that pop of color you need.</p>' +
        '<p>They can also protect your valuable artwork over time.</p>';
      this.productCategories.moulding.title = 'Frames';
      this.productCategories.backingBoard.title = 'Backing Boards';
      this.productCategories.showBag.title = 'Show Bag';
    }

    switch (this.builderType) {
      case 'FRAME': this.tileData = [{mainHeading: 'Style & Size'}, this.productCategories.moulding, this.sizeSelectorWithoutMats, {mainHeading: 'Optional Accessories'}, this.productCategories.topMat, this.productCategories.bottomMat, this.productCategories.backingBoard, this.productCategories.glazing]; break;
      case 'MAT':  this.tileData = [{mainHeading: 'Colors & Size'}, this.productCategories.topMat, this.productCategories.bottomMat, this.sizeSelectorWithMats, {mainHeading: 'Frames & Accessories'}, this.productCategories.moulding, this.productCategories.backingBoard, this.productCategories.glazing, this.productCategories.showBag]; break;
      case 'SAMPLE':  {
        this.productCategories.topMat.title = 'Matboards (2" x 4")';
        this.productCategories.topMat.helpInfo.message =  '<p>Matboards can enhance the over all look of your artwork and give you that pop of color you need.</p>' +
          '<p>They can also protect your valuable artwork over time.</p>';
        this.productCategories.moulding.title = 'Picture Frames (4")';
        this.productCategories.backingBoard.title = 'Backing Boards (4" x 6")';
        this.productCategories.glazing.title = 'Glazings (4" x 6")';
        this.productCategories.showBag.title = 'Show Bag (5" x 7")';
        this.tileData = [{mainHeading: 'Choose Samples'}, this.productCategories.samplePack, this.productCategories.topMat, this.productCategories.moulding, this.productCategories.backingBoard, this.productCategories.glazing, this.productCategories.showBag];
      } break;
    }

    this.abTestSubRef = SessionService.abTestsChanged.subscribe((abTests: any) => {
      // set default mats if A/B test is running
      if (abTests.default_mat) {
        const findFn = option => {
          return option.tagName === 'quality';
        };
        this.productCategories.topMat.filterOptions.find(findFn).selection = Object.getOwnPropertyNames(abTests.default_mat)[0];
        this.productCategories.bottomMat.filterOptions.find(findFn).selection = Object.getOwnPropertyNames(abTests.default_mat)[0];
      }

      // set default plexiglass if A/B test is running
      if (abTests.default_glazing) {
        this.productCategories.glazing.defaultProductCode = Object.getOwnPropertyNames(abTests.default_glazing)[0];
      }
    });

    this.chatSubRef = ChatService.chatStatusChanged.subscribe( (status) => {
      // case where it's available in new version means its always available (no toggling)
      if (this.chatStatus !== 'AVAILABLE' || status === 'AVAILABLE') {
        this.chatStatus = status;
      }
      if (this.getHelpWithDesignDeferredData !== null) {
        this.getHelpWithThisDesign(this.getHelpWithDesignDeferredData);
        this.getHelpWithDesignDeferredData = null;
      }
    });

    // set default product based on the builder used
    const defaultProductId = this.builderType ? environment.wc.defaultProducts[this.builderType.toLowerCase()] : null;

    // get predefined product
    const data = await this.products.predefinedProduct(params['predefinedProductId'] ? params['predefinedProductId'] : defaultProductId);

    // get the project data from the product (or ignore it if trying to generate from url params)
    let projectData = data ? data.projectData : null;

    // trying to generate from the URL
    if (params['outerSize']) {

      // start with empty object
      projectData = {};

      // check provided parameters against an empty project to see if they are valid entries for a project
      // and add special case property "openingSize" so legacy urls will continue to work
      const p = new Project({}, null);
      const acceptableProperties = Object.assign( {
        openingSize: null
      }, p.toJSON(false, false));
      p.destroy();

      Object.entries(params).forEach(([prop, propValue]) => {
        if (acceptableProperties.hasOwnProperty(prop) ) {
          if (prop.endsWith('Code')) {
            projectData[prop.replace('Code', '')] = {
              code: propValue
            };
          } else if (prop === 'openingSize') {
            const size = propValue && propValue !== '0x0' ? propValue.split('x') : null;
            projectData['openings'] = size ? [] : [];
            if (size) {
              projectData['openings'] = [
                {
                  'size': {
                    width: parseFloat(size[0]),
                    height: parseFloat(size[1])
                  }
                }
              ];
            } else {
              projectData['openings'] = [];
            }
          } else {
            projectData[prop] = isNaN(propValue) ? propValue : parseFloat(propValue);
          }
        }
      });

      // add a reveal to the opening if this is a double mat situation
      if (projectData.openings?.length > 0 && projectData.bottomMat) {
        projectData.openings[0].reveal = Project.DEFAULTS.reveal;
      }
    }

    // get the project via API
    if (!editMode && projectId) {
      this.project = await this
        .projectService
        .get(projectId, false);
    } else {
      this.ssrProjectLoad = editMode && projectId && !isPlatformBrowser(this.platformId);
      if (!this.ssrProjectLoad) {
        this.project = await this
          .projectService
          .getOrCreate(editMode && projectId || params['tempProjectId'] || (projectData && projectData.primaryProduct) || this.builderType, projectData);
      }
    }

    // still don't have a product, make one from defaults
    if (!this.project) {
      this.project = new Project({'offset': 0, 'openings': this.builderType === 'MAT' || !this.builderType ? [Opening.INDETERMINATE] : [], 'primaryProduct': this.builderType ? this.builderType : 'MAT' }, this.projectService);
      if (tempProjectId === 'temp') {
        const dialogRef = this.dialog.open(YesNoDialogComponent);
        const instance = dialogRef.componentInstance;

        instance.data = {
          title: 'No Unfinished Order',
          message: '<p>There are no unfinished orders to load.  Let\'s starting with a fresh order instead.</p>',
          yesButtonText: 'OK'
        };
      } else {
        this.errorLoadingProject = true;
      }
    }

    if (isPlatformBrowser(this.platformId)) {
      this.project.allowCustomization = this.cookieService.get('csr-mode') === 'true';
    }

    // in case it wasn't set previously, match the loaded product
    this.builderType = this.project.primaryProduct;

    if (this.project.numOpenings > 1) {
      this.sizeSelectorWithMats.title = 'Outer Size & Openings';
    }

    // pre-set qty to 1 for non-sample projects (where it's not already set)
    if (this.builderType !== 'SAMPLE' && !this.project.qty) {
      this.project.qty = 1;
    }

    // run again in case it changed from initial value
    switch (this.builderType) {
      case 'FRAME': this.tileData = [{mainHeading: 'Style & Size'}, this.productCategories.moulding, this.sizeSelectorWithoutMats, {mainHeading: 'Optional Accessories'}, this.productCategories.topMat, this.productCategories.bottomMat, this.productCategories.backingBoard, this.productCategories.glazing]; break;
      case 'MAT':  this.tileData = [{mainHeading: 'Colors & Size'}, this.productCategories.topMat, this.productCategories.bottomMat, this.sizeSelectorWithMats, {mainHeading: 'Frames & Accessories'}, this.productCategories.moulding, this.productCategories.backingBoard, this.productCategories.glazing, this.productCategories.showBag]; break;
      case 'SAMPLE':  {
        this.productCategories.topMat.title = 'Matboards (2" x 4")';
        this.productCategories.topMat.helpInfo.message =  '<p>Matboards can enhance the over all look of your artwork and give you that pop of color you need.</p>' +
          '<p>They can also protect your valuable artwork over time.</p>';
        this.productCategories.moulding.title = 'Picture Frames (4")';
        this.productCategories.backingBoard.title = 'Backing Boards (4" x 6")';
        this.productCategories.glazing.title = 'Glazings (4" x 6")';
        this.productCategories.showBag.title = 'Show Bag (5" x 7")';
        this.tileData = [{mainHeading: 'Choose Samples'}, this.productCategories.samplePack, this.productCategories.topMat, this.productCategories.moulding, this.productCategories.backingBoard, this.productCategories.glazing, this.productCategories.showBag];
      } break;
    }

    if (editMode && this.project.design && this.project.bottomMat) {
      this.productCategories.bottomMat.layers[0].projectDependencies = ['topMat', 'design'];
    }

    if (this.project.numOpenings > 1) {
      this.sizeHelpInfo.opening.helpInfo.yesButtonText = null;
      this.sizeHelpInfo.opening.helpInfo.message = this.sizeHelpInfo.opening.helpInfo.message.replace("<b>Having Trouble?</b> We'll calculate your opening size based on your artwork size for you!","");
    }

    const projectChanged = () => {
      this.disableLevelMessaging = false;

      if (this.project.primaryProduct === 'MAT') {
        this.sizeHelpInfo.outer = {
          heading: 'Outer Size',
          helpInfo: {
            title: 'Choosing the Right Outer Size',
            message: '<p><strong><u>With</u> Frame</strong></p>' +
              '<p>Your outer size should match the frame size as measured <i>from the back</i> to take into account the frame rabbet & matches the plexiglass size.</p>' +
              '<p><strong><u>Without</u> Frame</strong></p>' +
              '<p>No specific limitations up to our max of 32" x 40".</p>' +
              '<p class="image center"><img src="/assets/images/help/mat-layers.png" alt="Matboard Layers" /><br /><a target="_blank" href="/help/measuring-matboards">Matboard Measurement Guide</a></p>'
          }
        };
      } else if (this.project.primaryProduct === 'FRAME') {
        if (this.project.topMat) {
          this.sizeHelpInfo.outer = {
            heading: 'Outer Size',
            helpInfo: {
              title: 'Choosing the Right Outer Size',
              message: '<p>Outer size refers to the mat size and should match the frame size as measured <i>from the back</i> to take into account the frame rabbet.</p><p>Our maximum size is 32" x 40".</p>' +
                '<p class="image center"><img src="/assets/images/help/mat-layers.png" alt="Matboard Layers" /><br /><a target="_blank" href="/help/measuring-matboards">Matboard Measurement Guide</a></p>'
            }
          }
        } else {
          this.sizeHelpInfo.outer = {
            heading: 'Size',
            helpInfo: {
              title: 'Choosing the Right Size',
              message: '<p>Your frame size should match your artwork (whatever you are framing) as measured <i>from the back</i> of the frame to take into account the rabbet.</p><p>Our maximum size is 32" x 40".</p>' +
                '<p class="image center"><img src="/assets/images/help/outer-size.png" alt="Frame Outer Size" /><br /><a target="_blank" href="/help/measuring-matboards">Matboard Measurement Guide</a></p>'
            }
          };
        }
      } else {
        this.sizeHelpInfo.outer = {
          heading: 'Size',
          helpInfo: {
            title: 'Choosing the Right Size',
            message: '<p>We can custom cut up to a maximum size of 32" x 40".</p><p>If you intend to use this product in a frame, don\'t forget to match the frame size as measured <i>from the back</i> to take into account the frame rabbet.</p>'
          }
        };
      }

      let helpInfo = this.tileData[3].helpInfo;
      if (this.builderType === 'FRAME') {
        if (this.project.topMat) {
          this.tileData[2] = this.sizeSelectorWithMats;
        } else {
          this.tileData[2] = this.sizeSelectorWithoutMats;
        }
        helpInfo = this.tileData[2].helpInfo;
      }

      if (this.project.topMat || this.builderType === 'MAT') {
        helpInfo.message = this.sizeHelpInfo.outer.helpInfo.message + this.sizeHelpInfo.opening.helpInfo.message.replace('<p class="image center"><img src="/assets/images/help/mat-layers.png" alt="Matboard Layers"/><br /><a target="_blank" href="/help/measuring-matboards">Matboard Measurement Guide</a></p>','');
        helpInfo.yesClickedCallback = this.sizeHelpInfo.opening.helpInfo.yesClickedCallback;
        helpInfo.yesButtonText = this.sizeHelpInfo.opening.helpInfo.yesButtonText;
        helpInfo.yesClickedCallbackContext = this.sizeHelpInfo.opening.helpInfo.yesClickedCallbackContext;
      } else {
        helpInfo.message = this.sizeHelpInfo.outer.helpInfo.message;
      }

      const maxVGroove = this.project.availableVGrooveOffsets && this.project.allowVGroove ? this.project.availableVGrooveOffsets.max : 0;
      if ((this.project.vGrooveOffset > 0 && !this.project.allowVGroove ) || (this.project.vGrooveOffset > maxVGroove && maxVGroove > 0 && !this.project.design)) {
        const vGrooveState = this.project.vGrooveState;
        const dialogRef = this.dialog.open(YesNoDialogComponent);
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'V-Groove No Longer Available',
          yesButtonText: 'OK'
        };
        if (maxVGroove === 0) {
          if (vGrooveState.thinMargins) {
            instance.data.message = '<p>Your new margins are too narrow to allow for a V-Groove.  We have removed your V-Groove selection.</p>';
          } else if (vGrooveState.invalidMat) {
            instance.data.message = '<p>Black core and suede surface mats do not work well for V-Grooves.  We have removed your V-Groove selection. </p>';
          } else if (vGrooveState.blackTexturedSurface) {
            instance.data.message = '<p>Dark textured surface mats do not work well for V-Grooves.  We have removed your V-Groove selection. </p>';
          } else {
            instance.data.message = '<p>V-Groove is no longer available.  We have removed your V-Groove selection. </p>';
          }
          this.project.vGrooveOffset = 0;
        } else {
          instance.data.title = 'V-Groove Changed';
          instance.data.message = '<p>Your new margins are too narrow to allow for the V-Groove offset you selected.  We have reduced your v-groove offset to compensate.</p>';
          this.project.vGrooveOffset = maxVGroove;
        }
      }

      // if the project change was removal of top mat
      if (!this.project.bottomMat && this.productCategories.bottomMat.selection.length > 0) {
        this.productCategories.bottomMat.selection = [];
      }

      this.projectThumbs = {
        preview: this.project.url(100) + '&mod=' + this.projectThumbsImageCounter++,
        backing: this.project.backingBoard ? ('/assets/images/products/backingboards/' + this.project.backingBoard.code + '.jpg') : null,
        glazing: this.project.glazing ? ('/assets/images/products/glazings/' + this.project.glazing.code + '.jpg') : null,
        bag: this.project.bag ? ('/assets/images/products/bags/' + this.project.bag.code + '.jpg') : null
      };

      // if project backing is easel confirm it is still valid
      if (!this.project.isEaselReady || this.builderType === 'SAMPLES') {
        this.productCategories.backingBoard.filteredProducts = this.productCategories.backingBoard.products.filter(bb => bb.code !== environment.easelLimits.code);
        if (this.project.backingBoard && this.project.backingBoard.code === environment.easelLimits.code) {
          this.productCategories.backingBoard.selection = this.productCategories.backingBoard.products.filter(bb => bb.code === environment.easelLimits.fallbackBackingCode);
          this.project.backingBoard = this.productCategories.backingBoard.selection[0];
          const dialogRef = this.dialog.open(YesNoDialogComponent);
          const instance = dialogRef.componentInstance;
          const sizePipe = new SizePipe();
          instance.data = {
            title: 'Easel Backing No Longer Available',
            yesButtonText: 'OK',
            message: '<p>Easel backings are only available in select standard sizes &amp; ' + sizePipe.transform(environment.easelLimits.mouldingWidth) +
              '" or less frames that are suitable for table tops.  Consequently, we have <strong>changed your backing</strong> selection.</p>' +
              '<p>If you need a custom easel size, please contact us and we will do everything we can to help!</p>'
          }
        }
      } else {
        this.productCategories.backingBoard.filteredProducts = this.productCategories.backingBoard.products;
      }

      this.selectedCarouselTab = 0;
    };

    this.projectChangeSubRef = this.project.change.subscribe(() => projectChanged());

    this.projectSizeChangeSubRef = this.project.size.change.subscribe(() => {
      this.setProjectProperty('moulding', this.project.moulding);
      this.productCategories.moulding.products.forEach((product) => {
        product.disabled = this.project.size.longerDimension > product.maxLongDimension;
      });

      if (this.project.glazing) this.project.glazing.automateUpgradeIfNecessary = true;
      this.setProjectProperty('glazing', this.project.glazing);
      this.productCategories.glazing.products.forEach((product) => {
        product.disabled = product.thickness <= environment.glazingLimits.thickness && (this.project.size.shorterDimension > environment.glazingLimits.shorterDim || this.project.size.longerDimension > environment.glazingLimits.longerDim);
      });
    });

    const matData = await this.products.get('mats', true);
    matData.forEach(item => {
      if (!isNaN(item.thickness)) {
        item.thickness = item.thickness < 0.11 ? '4 Ply' : '8 Ply';
      }
    });

    this.productCategories.topMat.products = matData;
    if (this.project.topMat) {
      this.productCategories.topMat.selection = matData.filter((product) => {
        return product.code === this.project.topMat.code;
      })
      this.project.topMat = this.productCategories.topMat.selection[0];
    }

    this.productCategories.bottomMat.products = matData;
    if (this.project.bottomMat) {
      this.productCategories.bottomMat.selection = matData.filter((product) => {
        return product.code === this.project.bottomMat.code;
      })

      this.project.bottomMat = this.productCategories.bottomMat.selection[0];
    }

    const frameData = await this.products.get('frames', true);
    // drop 'wood frame' as visually it's too long for the pickers
    frameData.forEach(item => {
      item.disabled = item.maxLongDimension !== null && this.project.size.longerDimension > item.maxLongDimension;
      item.shortCategoryName = item.categoryName.replace(' Wood Frames', '');
    });
    this.productCategories.moulding.products = frameData;
    if (this.project.moulding) {
      this.productCategories.moulding.selection = frameData.filter((product) => {
        return product.code === this.project.moulding.code;
      })

      this.project.moulding = this.productCategories.moulding.selection[0];
    }

    const bbData = await this.products.get('backingboards', true);
    // to avoid headers, lets group all backing together
    bbData.forEach(item => {
      item.categoryName = 'Backing Board';
    });
    this.productCategories.backingBoard.products = bbData;
    if (this.project.backingBoard) {
      this.productCategories.backingBoard.selection = bbData.filter((product) => {
        return product.code == this.project.backingBoard.code;
      })
      this.project.backingBoard = this.productCategories.backingBoard.selection[0];
    }


    const glazingData = await this.products.get('glazings', true);
    // to avoid headers, lets group all plexi together
    glazingData.forEach(item => {
      item.categoryName = 'Plexiglass';
      item.disabled = item.thickness <= environment.glazingLimits.thickness && (this.project.size.shorterDimension > environment.glazingLimits.shorterDim || this.project.size.longerDimension > environment.glazingLimits.longerDim);
    });
    this.productCategories.glazing.products = glazingData;
    if (this.project.glazing) {
      this.productCategories.glazing.selection = glazingData.filter((product) => {
        return product.code === this.project.glazing.code;
      });
      this.project.glazing = this.productCategories.glazing.selection[0];
    }

    const shapeData = await this.products.get('shapes', true);
    this.project.availableShapes = new Array<Shape>();
    shapeData.forEach( item => {
      this.project.availableShapes.push(Shape.fromJson(item));
    });


    const samplePackData = await this.cmsService.merchData('samplePack');
    this.productCategories.samplePack.products = samplePackData.map(d => ({
        description: d.shortDescription,
        longDescription: d.description,
        thumb: d.image.src,
        fixedPrice: d.price,
        code: d.id
      })
    );
    if (this.productCategories.samplePack.products.length === 0 && this.builderType === 'SAMPLE') {
      this.tileData = this.tileData.filter(t => t.type !== 'SAMPLE_PACK');
    }

    let bagData = (await this.products.get('bags', true)).map( b => Object.assign({}, b));

    if (this.builderType === 'SAMPLE') {
      bagData = bagData.slice(0, 1);
      bagData[0].categoryName = 'Bags';
      bagData[0].longDescription = ' Sample (' + bagData[0].matSize + ')';
    } else {
      bagData.forEach(item => {
        item.categoryName = 'Bags';
        item.longDescription = ' for ' + item.matSize + ' Artwork & Mats'
      });
    }

    this.productCategories.showBag.products = bagData;
    if (this.project.bag) {
      this.productCategories.showBag.selection = bagData.filter((product) => {
        return product.code === this.project.bag.code;
      });
      this.project.bag = this.productCategories.showBag.selection[0];
    }

    if (this.builderType !== 'SAMPLE') {
      const filterAvailableBags = () => {
        this.productCategories.showBag.filteredProducts = this.productCategories.showBag.products.filter((bag) => {
          const rotatedMatSize = bag.matSize.split(' x ').reverse().join(' x ');
          return bag.matSize === this.project.size.toString() || rotatedMatSize === this.project.size.toString();
        });
      };
      filterAvailableBags();

      this.projectSizeChange2SubRef = this.project.size.change.subscribe((changed) => {
        filterAvailableBags();

        if (this.productCategories.showBag.selection.length > 0 || this.productCategories.showBag.wasAutoDeselected) {
          this.productCategories.showBag.selection = this.productCategories.showBag.filteredProducts[0] ? [this.productCategories.showBag.filteredProducts[0]] : [];
          if (this.productCategories.showBag.selection.length == 0) {
            this.productCategories.showBag.wasAutoDeselected = true;
          }
          this.project.bag = this.productCategories.showBag.selection[0];
        }
      });
    }
    else {
      this.productCategories.showBag.filteredProducts = null;

      ['topMat','moulding','backingBoard','glazing','showBag'].forEach( prod => {
        let cat = this.productCategories[prod];
        let samples = this.getProjectSamples(cat.type);
        cat.selection = !samples ? [] : cat.products.filter(product => {
          return samples.filter(sample => sample.code === product.code).length === 1;
        });
      });
    }

    if (this.redirectMatboardUrlWithoutCoreIfNecessary(params)) return;

    // don't pick default from the categories if this was set using the url
    if (!params['outerSize']) {
      if (params['primaryProduct'] === 'FRAME') {
        if (params['description']) {
          const newValue = this.productCategories.moulding.products.filter((product) => {
            return this.partDisplay.description(product).trim().replace(/[®\/]/g, '').toLowerCase() === params['description']
              && product.categoryName.replace(' Wood Frames', '').toLowerCase() === params['category'];
          });
          if (newValue) {
            this.onSelectedProductChange(newValue, this.productCategories.moulding);
          }
        } else {
          this.projectEditRequested('moulding');
        }
      } else if (params['primaryProduct'] === 'MAT') {
        const newValue = this.productCategories.topMat.products.filter((product) => {
          return (product.quality.trim().toLowerCase() == params['quality'] || typeof params['quality'] === 'undefined') &&
            (product.coreType.toLowerCase() === params['core'] || typeof params['core'] === 'undefined') &&
            (product.thickness.toLowerCase() === params['thickness'] || typeof params['thickness'] === 'undefined') &&
            (product.finish.toLowerCase() === params['finish'] || typeof params['finish'] === 'undefined');
        });
        if (newValue) {
          this.onSelectedProductChange(newValue, this.productCategories.topMat);
        }
      }
    }

    if (upsellProperty) {
      this.projectEditRequested(upsellProperty);
    }

    // if an underlying project (via a product or otherwise) or generated by url is involved
    const projectSeoData = this.seoService.projectSeoData(this.project);
    if (!editMode && (params["predefinedProductId"] || params['predefinedProductProjectId'] || params['projectId'] || params['outerSize']) && projectSeoData?.metaTitle) {
      this.seoService.setMetaData({
        title: projectSeoData.metaTitle,
        description: projectSeoData.metaDescription,
        og: {
          image: this.project.url(500)
        }
      });
    }
    // NOTE: By not setting any meta data here, we accept the defaults set by
    // checking URL against meta data defined in WP dynamic text.
    // In this case, this applies for links like /buy/matboard and /buy/matboard/other-deeper-links

    // add remarketing data for the current builder type
    this.seoService.trackAdWordsRemarketingInfo('builder', this.builderType);

    /* COMMENTING OUT SINCE PRICES NOT CURRENTLY USED --
    this.setProductPrices();
    */

    // THIS CODE IS FOR DEFAULTING THE PRIMARY SELECTOR IF BUILDER IS EMPTY
    // WE ARE NOT USING IT RIGHT NOW BUT MIGHT IN THE FUTURE
    /*
    // if an empty mat or frame builder, then open the main selector to get customer started
    if (this.project.isEmpty && (this.project.primaryProduct === 'MAT' || this.project.primaryProduct === 'FRAME')) {
      this.openSelectorDialog(this.project.primaryProduct === 'MAT' ? this.productCategories.topMat : this.productCategories.moulding);
    }
     */

    this.project.triggerChange();
    setTimeout( () => {
      this.project.allowUpdates();
      this.ready = true;
    });
  };

  setShowBagSize(results): void {
    this.project.size.width = results.outer[0];
    this.project.size.height = results.outer[1];
    this.project.bag = this.productCategories.showBag.products.filter( (bag) => {
      return bag.matSize == this.project.size.toString();
    })[0];
  }

  @ViewChild('mobileMatSelector') mobileMatSelector:MatSelectorComponent;
  private scrollPastLargePreview() {
    if (this.media.isMatched(BREAKPOINTS.Standard.XSmall) && this.carouselComponent) {
      let scrollAmount = this.carouselComponent.elementRef.nativeElement.getBoundingClientRect().height;
      this.window.document.scrollingElement.scrollTo(0, scrollAmount);
      if (this.mobileMatSelector) {
        setTimeout(() => {
          this.mobileMatSelector.resetPosition();
        }, 0);
      }
    }
  }

  private _scrolling:boolean = false;
  public scrollToTop() {

    if (isPlatformBrowser(this.platformId)) {
      this._scrolling = true;

      // easing functions http://goo.gl/5HLl8
      let easeInOutQuad = function (t, b, c, d) {
        t /= d / 2;
        if (t < 1) {
          return c / 2 * t * t + b
        }
        t--;
        return -c / 2 * (t * (t - 2) - 1) + b;
      };

      // requestAnimationFrame for Smart Animating http://goo.gl/sx5sts
      let __this = this;
      let requestAnimFrame = (function () {
        return __this.window && __this.window.requestAnimationFrame || __this.window['webkitRequestAnimationFrame'] || __this.window['mozRequestAnimationFrame'] || function (callback) {
          __this.window.setTimeout(callback, 1000 / 60);
        };
      })();

      let start = document.scrollingElement.scrollTop,
        change = -start,
        currentTime = 0,
        increment = 20,
        duration = 250;

      let animateScroll = () => {
        currentTime += increment;
        document.scrollingElement.scrollTop = easeInOutQuad(currentTime, start, change, duration);
        if (currentTime < duration) {
          requestAnimFrame(animateScroll);
        } else {
          this._scrolling = false;
        }
      };
      animateScroll();
    }
  }

  removeSample($event, sample:any) {
    $event.stopPropagation();
    this.project.removeSample(sample);
    let prop = '';
    switch (sample.type) {
      case 'MAT': prop = 'topMat'; break;
      case 'FRAME': prop = 'moulding'; break;
      case 'BACKINGBOARD': prop = 'backingBoard'; break;
      case 'GLAZING': prop = 'glazing'; break;
      case 'SHOWBAG': prop = 'showBag'; break;
    };

    if (prop)
      this.productCategories[prop].selection = this.productCategories[prop].selection.filter( p => {
      return p !== sample;
    });
  }

  upsellsCompleted = false;
  upsells: any = {
    bottomMat: false,
    moulding: false
  };

  async addToCartClicked($event = null) {
    if ($event) $event.currentTarget.blur();

    let valid = true;
    // validate the project before adding to cart
    try {
      this.project.validate();
    } catch (error: any) {
      valid = false;
      this.handleProjectError(error.message);
      this.handleProjectWarning(error.message);
    }

    if (valid) {

      if (!this.upsellsCompleted && this.project.primaryProduct === 'MAT' && !this.project.originalId) {
        const originalProjectPrice = this.project.price;
        if (this.upsells.bottomMat === false && !this.project.bottomMat && this.project.openings.length === 1 && !this.project.design) {
          const projectJSON = this.project.toJSON();
          delete projectJSON.previewQueryStringParams;
          projectJSON.topMat = {
            code: projectJSON.topMatCode
          }
          projectJSON.bottomMat = {
            code: projectJSON.topMatCode
          }
          projectJSON.reveal = 0.25;
          projectJSON.id = null;
          const upsellProject = new Project(projectJSON, null);

          const margins = upsellProject.margins;
          const section = (upsellProject.openings as Array<Opening>).length === 0 ? null : (margins.left - 1) + 'x' + (margins.top - 1) + 'x' + (upsellProject.reveal + 2) + 'x' + (upsellProject.reveal +  2);
          const imageUrl = upsellProject.url(250, null, section);
          upsellProject.destroy();

          const dialogRef1 = this.dialog.open(YesNoDialogComponent, {});
          const instance1 = dialogRef1.componentInstance;
          instance1.data = {
            title: 'Include an accent mat',
            closeMethod: 'x',
            message: '<p class="center">We recommend that you <strong>add an accent mat</strong> to your project.</p>' +
              '<p class="image shadow center"><img alt="accent mat upsell preview" src="' + imageUrl + '" /></p>' +
              '<p class="center">Adding a second layer of matting gives you more depth and color.</p>',
            yesButtonText: 'Choose Accent Mat',
            noButtonText: 'No Thanks'
          };

          dialogRef1.afterClosed().subscribe( (result) => {
            this.upsells.bottomMat = true;
            if (result === false) {
              this.addToCartClicked();
            } else {
              this.projectEditRequested('bottomMat');
            }
          });
          return;
        }

        if (this.upsells.moulding === false && !this.project.moulding && this.project.openings.length > 0) {
          const projectJSON = this.project.toJSON();
          projectJSON.id = null;
          delete projectJSON.previewQueryStringParams;
          if (projectJSON.topMatCode) {
            projectJSON.topMat = {
              code: projectJSON.topMatCode
            }
          }
          if (projectJSON.bottomMatCode) {
            projectJSON.bottomMat = {
              code: projectJSON.bottomMatCode
            }
          }
          projectJSON.moulding = { code: 'FR26099' };
          if (!projectJSON.glazing) projectJSON.glazing = { code: 'PLEXIGLASS' };
          if (!projectJSON.backingBoard) projectJSON.backingBoard = { code: 'FOAM' };
          const upsellProject = new Project(projectJSON, null);

          const result = await this.projectService.getPrice(upsellProject);

          const delta = result.price - originalProjectPrice;
          const imageUrl = upsellProject.url(250, null, null);
          upsellProject.destroy();

          const currencyPipe = new CurrencyPipe('en');
          const dialogRef2 = this.dialog.open(YesNoDialogComponent, {});
          const instance2 = dialogRef2.componentInstance;
          instance2.data = {
            title: 'Add a frame',
            closeMethod: 'x',
            message: '<p class="center"><strong>Order a picture frame</strong> that will fit your mat perfectly.</p>' +
              '<p class="image shadow center"><img alt="frame upsell preview" src="' + imageUrl + '" />' +
              '<span class="overlay upsell">' + currencyPipe.transform(delta) + '</span></p>' +
              '<p class="center">Choose from our many frame styles!</p>',
            yesButtonText: 'Choose Frame',
            noButtonText: 'No Thanks'
          };

          dialogRef2.afterClosed().subscribe((res) => {
            this.upsells.moulding = true;
            if (res === false) {
              this.addToCartClicked();
            } else {
              this.projectEditRequested('moulding');
            }
          });
          return;
        }
        this.upsellsCompleted  = true;
      }

      const dialogRef = this.dialog.open(WaitModalComponent, {});

      const instance = dialogRef.componentInstance;
      dialogRef.disableClose = true;
      instance.state = 'PROCESSING';
      if (this.project.originalId) {
        instance.titles = {
          PROCESSING: 'Saving Changes...',
          ERROR: 'Save Changes Error',
          DONE: 'Saved Changes'
        };

        instance.text = {
          PROCESSING: 'Please wait while we save your changes.',
          ERROR: 'We encountered an unexpected error while saving your changes.',
          DONE: 'Your changes have been saved.  Returning to the ' + (this.currentCartIsOrder ? 'order.' : 'cart.')
        };
      } else {
        instance.titles = {
          PROCESSING: this.currentCartIsOrder ? 'Adding to Order...' : 'Adding to Cart...',
          ERROR: this.currentCartIsOrder ? 'Add to Order Error' : 'Add to Cart Error',
          DONE: this.currentCartIsOrder ? 'Added to Order' : 'Added to Cart'
        };

        instance.text = {
          PROCESSING: this.currentCartIsOrder ? 'Please wait while we add your item to your order.' : 'Please wait while we add your order to cart.',
          ERROR: this.currentCartIsOrder ? 'We encountered an unexpected error while adding to your order.' : 'We encountered an unexpected error while adding to cart.',
          DONE: this.currentCartIsOrder ? 'Your item has been added to your order.' : 'Your order has been added to cart.'
        };

        instance.yesButton = {
          text: this.currentCartIsOrder ? 'Review Order' : 'View Cart'
        };

        instance.noButton = {
          text: 'Continue Shopping'
        };
      }

      dialogRef.afterClosed().subscribe( (result: boolean) => {
        if (result) {
          this.router.navigateByUrl(this.currentCartIsOrder ? '/order' : '/cart');
        } else {
          this.project.id = null;
          if (this.builderType === 'SAMPLE') {
            this.project.clearSamples();

            // clear all settings except for size/openings from UI
            this.productCategories.topMat.selection = [];
            this.productCategories.backingBoard.selection = [];
            this.productCategories.moulding.selection = [];
            this.productCategories.glazing.selection = [];
            this.productCategories.showBag.selection = [];
          }
        }
      });

      if (this.project.originalId) {
        // swap project back as its time to save it
        this.project.id = this.project.originalId;
        try {
          await this.cartService.updateCartItem(this.project.toJSON());

          // when we update an item in the cart leave messaging should be disabled
          this.disableLevelMessaging = true;

          // set time out is so animation can finish
          setTimeout( () => {
            this.router.navigateByUrl(this.currentCartIsOrder ? '/order' : '/cart');
          }, 1000);

          instance.state = 'DONE';
        } catch {
          instance.state = 'ERROR';
        }
      } else {
        try {
          const result = await this.cartService.addProject(this.project);
          // when we add to cart leave messaging should be disabled
          this.disableLevelMessaging = true;

          // track the add to cart in google analytics
          this.seoService.trackGAEcommEvent('add_to_cart', result.orderlines[result.orderlines.length - 1]);

          // mark modal as done
          instance.state = 'DONE';
        } catch {
          instance.state = 'ERROR';
        }
      }
    }
  }

  public clearAll($event):void {
    $event.currentTarget.blur();
    let dialogRef = this.dialog.open(YesNoDialogComponent);
    let instance = dialogRef.componentInstance;

    instance.data = {
      title: 'Clear All Selections?',
      message: '<p>Are you sure you want to clear all selections and start over?</p>',
      yesButtonText: 'Yes',
      noButtonText: 'No'
    };

    dialogRef.afterClosed().subscribe( (result: boolean) => {
      if (result) {
        // clear all settings except for size/openings from project
        this.project.topMat = null;
        this.project.bottomMat = null;
        this.project.backingBoard = null;
        this.project.moulding = null;
        this.project.glazing = null;
        this.project.bag = null;
        this.project.print = null;
        this.project.clearSamples();

        // reset size & openings if necessary (not for multi opening)
        if (this.project.numOpenings <= 1) {
          // reset outer size
          this.project.size.width = NaN;
          this.project.size.height = NaN;

          this.project.clearOpenings();

          if (this.builderType === 'MAT') {
            const opening: Opening = new Opening(new Size(NaN, NaN), [Shape.DEFAULT])
            this.project.addOpening(opening);
          }
        }

        // clear all settings except for size/openings from UI
        this.productCategories.topMat.selection = [];
        this.productCategories.bottomMat.selection = [];
        this.productCategories.backingBoard.selection = [];
        this.productCategories.moulding.selection = [];
        this.productCategories.glazing.selection = [];
        this.productCategories.showBag.selection = [];
        this.productCategories.samplePack.selection = [];
      }
    });
  }

  private setProjectProperty(prop:string, val:any, silent:boolean = false) {
    try {
      this.project[prop] = val;
    } catch (error: any) {
      setTimeout( () => {
        this.handleProjectError(error.message, error.data, silent);
      }, 0);
    }
  }

  private handleProjectError(err:string, data:any=null, silent:boolean=false) {
    switch (err) {
      case 'THIN_GLAZING_NOT_ALLOWED': {
        this.productCategories.glazing.products.forEach( product => {
          product.disabled = product.thickness <= environment.glazingLimits.thickness && (this.project.size.shorterDimension > environment.glazingLimits.shorterDim || this.project.size.longerDimension > environment.glazingLimits.longerDim);
          if (data.oldValue && data.oldValue.code === product.code) {
            data.oldValue.disabled = product.disabled;
          }
          if (product.code === (data.oldValue && !data.oldValue.disabled ? data.oldValue.code : this.productCategories.glazing.thickerDefaultProductCode)) {
            this.productCategories.glazing.selection = [product];
          }
        });

        this.project.glazing = this.productCategories.glazing.selection[0];
        this.project.glazing.automaticallySet = data.attemptedValue.automaticallySet;

        if (!silent) {
          const dialogRef = this.dialog.open(YesNoDialogComponent, {});
          const instance = dialogRef.componentInstance;
          instance.data = {
            closeMethod: 'x'
          };

          if (data.attemptedValue.automateUpgradeIfNecessary) {
            instance.data.title = 'Plexiglass Upgraded';
            instance.data.message = 'Large standard plexiglass is more prone to bending due to its thinness which can cause issues for certain applications. We\n' +
              'have upgraded you to a thicker premium option.';
          } else {
            instance.data.title = 'Large Standard Plexiglass';
            instance.data.message = 'Large standard plexiglass is not available at this size.  At large sizes (over 20"), it is more prone to bending due to its thinness which can cause issues for certain applications.';
            if (!data.oldValue || this.productCategories.glazing.selection[0].code !== data.oldValue.code) {
              instance.data.message += ' We have upgraded you to a thicker premium option.';
            }
          }
        }
        break;
      }
      case 'THIN_MDF_NOT_ALLOWED': {
        let matchFound = false;
        this.productCategories.moulding.selection = [];
        this.productCategories.moulding.products.forEach( product => {
          if (typeof product.coreColor !== 'undefined' && product.coreColor === data.attemptedValue.coreColor && product.maxLongDimension > this.project.size.longerDimension) {
            this.productCategories.moulding.selection = [product];
            matchFound = true;
          }
        });

        if (matchFound) {
          this.project.moulding = this.productCategories.moulding.selection[0];
        } else {
          this.removeProductSelection(null, {
            projectProperty: 'moulding',
            selection: null,
            type: 'FRAME'
          });
        }

        if (!silent) {
          const dialogRef = this.dialog.open(YesNoDialogComponent, {});

          const instance = dialogRef.componentInstance;
          instance.data = {
            title: 'Wider Frame Required',
            closeMethod: 'x',
            message: '<p>Out thinner frames can bend or flex, or get damaged in shipping when any side is greater than 30".</p>'
          };

          if (matchFound) {
            instance.data.message += '<p>We have selected the equivalent wider version of this frame to ensure it arrives safely and is of excellent quality.</p>';
          } else {
            instance.data.message += '<p>Please choose a different frame to ensure it arrives safely and is of excellent quality.</p>';
          }
        }
        break;
      }
      case 'MARGINS_TOO_THIN': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Your Border is Too Thin',
          closeMethod:  'button',
          yesButtonText: 'Change Size'
        };

        if (this.project.size.ui > environment.minMargins.ui) {
          instance.data.message = '<p>Large (about 16" x 20") matboards with thin borders get damaged in shipping and generally don\'t look very good.</p>' +
            '<p>Please reduce your opening size, or increase your outer size to choose a top color border that is at least 1" thick.</p>';

        } else {
          instance.data.message = '<p>Matboards with thin borders get damaged in shipping and generally don\'t look very good.</p>' +
            '<p>Please reduce your opening size, or increase your outer size to choose a top color border that is at least 1/2" thick.</p>';
        }

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.openSelectorDialog(this.builderType === 'FRAME' ? this.sizeSelectorWithoutMats : this.sizeSelectorWithMats);
          }
        });
        break;
      }

      case 'PROJECT_UPDATING': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Please Try Again',
          message: '<p>It\'s taking a while to save your changes.  Please try again in a few moments.</p>'
        };
        break;
      }

      case 'NO_QTY': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Specify a Quantity',
          message: '<p>You forgot to specify a quantity.  Please enter how many you want.</p>'
        };
        break;
      }

      case 'INCOMPLETE_OPENING': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Choose Your Mat Opening',
          message: '<p>You forgot to specify an opening size.  If you don\'t want an opening, remove it before proceeding.</p>'
        };
        break;
      }

      case 'OPENING_TOO_SMALL_FOR_CORNER': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Opening Size is Too Small',
          message: '<p>More intricate custom corners may not cut properly for very small openings.  Please choose an opening size of 3" x 3" or more for any opening with custom corners if you wish to proceed.</p>' +
                   '<p>Every case is unique so if you prefer, you can <a href="/contact-us">contact us</a> to discuss your specific needs to see what we can do to help.</p>'
        };
        break;
      }

      case 'INCOMPLETE_SIZE': {
        let title = 'Choose Your Product Size';
        switch (this.project.primaryProduct) {
          case 'MAT': title = 'Choose Your Mat Size'; break;
          case 'FRAME': title = 'Choose Your Frame Size'; break;
        }

        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: title,
          message: '<p>You forgot to specify a size.  Please enter your product size.</p>'
        };
        break;
      }

      case 'SIZE_TOO_LARGE': {
        let title = 'Maximum Size Exceeded';
        switch (this.project.primaryProduct) {
          case 'MAT': title = 'Your Mat Is Too Big'; break;
          case 'FRAME': title = 'Your Frame Is Too Big'; break;
        }

        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: title,
          message: '<p>The size entered exceeds the maximum allowed size (32" x 40"). ' +
                    'Please choose a smaller size, if possible.</p>'
        };
        break;
      }

      case 'MISSING_REQUIRED_PART': {
        let title = 'Choose A Mat';
        switch (this.project.primaryProduct) {
          case 'MAT': title = 'Choose a Primary Mat'; break;
          case 'FRAME': title = 'Choose a Frame'; break;
        }

        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: title,
          message: '<p>You forgot to choose a ' + this.project.primaryProduct.toLowerCase() + '.  Please do so to continue.</p>'
        };
        break;
      }

      case 'MISSING_REQUIRED_MAT': {
        const msg = this.project.restrictions.requiredMats === 2 ? 'both a primary & accent mat' : 'a primary mat';
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Choose Required Mats',
          message: '<p>This custom design requires you to choose ' + msg + '. Please do so to continue.</p>'
        };
        break;
      }

      case 'THIN_BACKING_BOARD': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Thicker Backing Required',
          message: '<p>In our experience, frames require a thick backing board such as our foam offerings for best results.</p>' +
                   '<p>If you have your own backing, please remove your selected backing to continue.</p>' ,
          yesButtonText: 'Switch to Foam & Continue',
          noButtonText: 'Remove Backing & Continue'
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.project.backingBoard = this.productCategories.backingBoard.products.filter( (bb) => {
              return bb.thickness >= environment.minBackingThicknessForFrames && bb.quality === this.project.backingBoard.quality;
            })[0];
            this.productCategories.backingBoard.selection = [this.project.backingBoard];
          } else {
            this.project.backingBoard = null;
            this.productCategories.backingBoard.selection = [];
          }
          this.addToCartClicked();
        });
      } break;

      case 'MAT_WITH_NON_GLARE': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Non Glare Plexiglass & Mats',
          message: '<p>Non glare plexiglass works best when it makes direct contact with the art/print being framed.  Otherwise, the ' +
                   'art or print can appear blurry under the plexiglass.  Mats create a slight gap between ' +
                   'the art and the glazing.  In such instances we recommend switching to our premium plexiglass for best results.</p>',
          yesButtonText: 'Switch to Premium & Continue',
          noButtonText: 'Cancel'
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.project.glazing = this.productCategories.glazing.products.filter( (glazing) => {
              return glazing.code.indexOf('PREMIUM') !== -1;
            })[0];
            this.productCategories.glazing.selection = [this.project.glazing];
            this.addToCartClicked();
          }
        });
      } break;

      case 'MAT_CORE_MISMATCH': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Mismatched Mat Cores',
          message: '<p>You have combined mats that have different core colors.  In our experience, this doesn\'t produce the best ' +
            'results. If this was intentional, please contact us, and we will be happy to help.</p>',
          yesButtonText: 'Change Selection',
          noButtonText: 'Contact Us',
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            const oldBottomMat = this.project.bottomMat;
            this.project.bottomMat = null;
            this.productCategories.bottomMat.filterOptions = this.productCategories.topMat.filterOptions;
            this.openSelectorDialog(this.productCategories.bottomMat);
            setTimeout(() => {
              this.project.bottomMat = oldBottomMat;
            }, 500);
          } else {
            this.router.navigateByUrl('/contact-us');
          }
        });
      } break;

      case 'MAT_THICKNESS_MISMATCH': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Mismatched Mat Thickness',
          message: '<p>You have combined mats that have different thicknesses.  In our experience, this doesn\'t produce the best ' +
            'results. If this was intentional, please contact us, and we will be happy to help.</p>',
          yesButtonText: 'Change Selection',
          noButtonText: 'Contact Us',
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            const oldBottomMat = this.project.bottomMat;
            this.project.bottomMat = null;
            this.productCategories.bottomMat.filterOptions = this.productCategories.topMat.filterOptions;
            this.openSelectorDialog(this.productCategories.bottomMat);
            setTimeout(() => {
              this.project.bottomMat = oldBottomMat;
            }, 500);
          } else {
            this.router.navigateByUrl('/contact-us');
          }
        });
      } break;
    }
  }

  private handleProjectWarning(err:string, data:any=null) {
    switch (err) {
      case 'OPENING_MATCHES_ARTWORK': {
        const sizePipe = new SizePipe();
        const inchesPipe = new InchesPipe();

        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.disableClose = true;
        const instance = dialogRef.componentInstance;
        instance.data = {
          title: 'Double Check Opening Size',
          message: '<p>Your opening size matches a common artwork size. Opening size should be <strong>1/2" smaller</strong> '  +
                  'than your artwork size to avoid gaps. If you entered your artwork size by mistake, select "correct opening size" before continuing.</p>',
          yesButtonText: this.project.originalId ? 'Save Changes' : 'Add to Cart',
          yesVerifierText: 'Correct Opening Size to ' + inchesPipe.transform(sizePipe.transform([this.project.opening.size.width - 0.5, this.project.opening.size.height - 0.5]))
        };

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.project.opening.size.width -= 0.5;
            this.project.opening.size.height -= 0.5;
            this.project.bottomMargin += 0.25;
          }

          this.project.clearWarning('OPENING_MATCHES_ARTWORK');
          this.addToCartClicked();
        });
      } break;

      case 'OPENING_SHAPE_MISMATCH': {
        const dialogRef = this.dialog.open(YesNoDialogComponent, {});
        const instance = dialogRef.componentInstance;
        instance.data = {
          closeMethod: 'x',
          title: 'Custom Shape is Distorted',
          message: '<p>Your opening size and shape are outside the normal range.  This can result in a distorted shape, and in some cases a bad cut quality.</p>' +
                   '<p>We can automatically correct the shape of your opening to be within range but you will need to review the results to make sure they will work for your project.</p>',
          yesButtonText: 'I Understand',
          noButtonText: 'Fix My Opening'
        };

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.project.clearWarning('OPENING_SHAPE_MISMATCH');
            this.addToCartClicked();
          } else {
            this.project.opening.size = Project.adjustSizeToRatio(this.project.opening.size, this.project.opening.shape[0].aspectRatio);

            const dialogRef2 = this.dialog.open(YesNoDialogComponent, {});
            const instance2 = dialogRef2.componentInstance;
            instance2.data = {
              closeMethod: 'x',
              title: 'Opening Changed',
              message: '<p>Your opening size was changed to better match the shape you selected. Please confirm it still works for your project before continuing.</p>',
              yesButtonText: 'I Understand'
            };
          }
        });
      } break;
    }
  }

  editCancelled:boolean = false;
  cancelEdit() {
    this.editCancelled = true;
    if (this.currentCartIsOrder) {
      this.router.navigateByUrl('/order');
    } else {
      this.router.navigateByUrl('/cart');
    }
  }

  projectEditRequested($event):void {
    if ($event === 'SIZE') {
      const sizeData = this.tileData.find( tile => {
        return tile.type === 'SIZE-SELECTOR';
      });
      if (sizeData) {
        this.openSelectorDialog(sizeData);
      }
    } else {
      this.openSelectorDialog(this.productCategories[$event]);
    }
  }

  openSelectorDialog(productCategory: any) {
    if (productCategory.projectProperty === 'topMat' && this.project.topMat || productCategory.projectProperty === 'bottomMat' && this.project.topMat && !this.project.bottomMat) {
      productCategory.filterOptions[0].selection = this.project.topMat.quality;
      productCategory.filterOptions[1].selection = this.project.topMat.coreType;
      productCategory.filterOptions[2].selection = this.project.topMat.thickness;
    } else if (productCategory.projectProperty === 'bottomMat' && this.project.bottomMat) {
      productCategory.filterOptions[0].selection = this.project.bottomMat.quality;
      productCategory.filterOptions[1].selection = this.project.bottomMat.coreType;
      productCategory.filterOptions[2].selection = this.project.bottomMat.thickness;
    }

    if (productCategory.projectProperty === 'bottomMat' && !this.project.bottomMatAllowed) {
      const dRef = this.dialog.open(YesNoDialogComponent, {});
      dRef.componentInstance.data = {
        title: 'Why Can\'t I Make Changes?',
        message: '<p>This project does not support double mats.</p>',
        yesButtonText: 'OK'
      };

      if (!this.project.design) {
        dRef.componentInstance.data.message += 'We understand that this may not meet your needs and are more than happy to help you place a custom order instead.</p>' +
          '<p>Would you like to discuss your custom order with one of our customer service representatives?</p>';
        dRef.componentInstance.data.yesButtonText = 'Yes';
        dRef.componentInstance.data.noButtonText = 'No';
        dRef.afterClosed().subscribe(goToRequestQuotePage => {
          if (goToRequestQuotePage) {
            this.disableLevelMessaging = true;
            this.router.navigateByUrl('/custom-matboard-design-quote')
          }
        });
      }

      return;
    }

    var catState = this.determineCategoryState(productCategory);
    if (catState === 'locked') {
      // old condition: this.project.permanentDesignId && (productCategory.type === 'SIZE-SELECTOR' || (this.project.numMats === 1 && productCategory.projectProperty === 'bottomMat'))

      const dRef = this.dialog.open(YesNoDialogComponent, {});
      dRef.componentInstance.data = {
        title: 'Why Can\'t I Make Changes?',
        message: '<p>You are ordering a project that does not allow size edits.</p>',
        yesButtonText: 'OK'
      };
      if (!this.project.design) {
        dRef.componentInstance.data.message += 'We understand that this may not meet your needs and are more than happy to help you place a custom order instead.</p>' +
          '<p>Would you like to discuss your custom order with one of our customer service representatives?</p>';
        dRef.componentInstance.data.yesButtonText = 'Yes';
        dRef.componentInstance.data.noButtonText = 'No';
        dRef.afterClosed().subscribe(goToRequestQuotePage => {
          if (goToRequestQuotePage) {
            this.disableLevelMessaging = true;
            this.router.navigateByUrl('/custom-matboard-design-quote')
          }
        });
      }
      return;
    }

    const dialogRef = this.dialog.open(SelectorDialogComponent, {
      panelClass: 'fullscreen',
      maxWidth: (productCategory.type === 'SHOWBAG' || productCategory.type === 'GLAZING') && !this.media.isMatched(BREAKPOINTS.Standard.XSmall) ? '560px' : '85vw',
      width: '820px',
      disableClose: productCategory.type === 'SIZE-SELECTOR' || this.builderType === 'SAMPLE'
    });

    // set filters for when we show mats
    if (productCategory.type === 'MAT') {
      productCategory.filteredProducts = this.products.filterByTags(productCategory.products, this.products.getTags(productCategory.filterOptions));
    }

    const instance = dialogRef.componentInstance;
    instance.project = this.project;
    instance.samplesMode = this.builderType === 'SAMPLE';
    instance.productCategory = productCategory;
    instance.sizeHelpInfo = this.sizeHelpInfo;
    instance.showSamplesButton = productCategory.showSamplesButtonInSelector;

    if (this.builderType === 'SAMPLE' && instance.productCategory.type === 'SHOWBAG') {
      instance.specialInstructions = '<strong>Please note</strong> that we only offer 5x7 show bag samples because bigger ones are too large for our samples packaging.';
    }

    const _selectedProdChangeSubRef = instance.selectedProductChange.subscribe((newValues: any) => {
      this.onSelectedProductChange(newValues, productCategory);

      if (this.builderType !== 'SAMPLE') {
        if (productCategory.scrollToTopAfterSelection) {
          dialogRef.afterClosed().subscribe( () => {
            if (instance.selectedProductChange && !instance.selectedProductChange.closed) instance.selectedProductChange.unsubscribe();
            if (instance.bottomMatChangedBySizeSelector && !instance.bottomMatChangedBySizeSelector.closed) instance.bottomMatChangedBySizeSelector.unsubscribe();
            this.scrollToTop();
          });
        }
        dialogRef.close();
      }
    });

    const _bottomMatChangedBySizeSubRef = instance.bottomMatChangedBySizeSelector.subscribe( (newValues) => {
      this.onSelectedProductChange(newValues, this.productCategories.bottomMat);
    });

    dialogRef.afterClosed().subscribe((result:string) => {
      _selectedProdChangeSubRef.unsubscribe();
      _bottomMatChangedBySizeSubRef.unsubscribe();

      if (result === 'NAVIGATE_TO_CUSTOM_DESIGNS') {
        this.disableLevelMessaging = true;
        this.router.navigateByUrl('/shop/custom-matboard-designs');
      } else if (result === 'NAVIGATE_TO_SAMPLES') {
        this.disableLevelMessaging = true;
        this.router.navigateByUrl('/buy/samples');
      }
    });
  }

  resolveProp(obj, prop): any {
    if (!obj) return null;
    prop = prop.split('.');
    const operator = prop[0][prop[0].search(/[=><]/)];
    if (operator) {
      prop = prop[0].split(operator);
      switch (operator) {
        case '=': return obj[prop[0]] == prop[1];
        case '<': return obj[prop[0]] < prop[1];
        case '>': return obj[prop[0]] > prop[1];
      }
    } else {
      obj = obj[prop[0]];
      if (prop[1]) {
        return this.resolveProp(obj, prop[1]);
      } else {
        return obj;
      }
    }
  }

  missingDependencies(productCategory): number {
    let missingDepIndex = 0;
    if (productCategory.dependencies) {
      for (let i = 0; i < productCategory.dependencies.length; i++) {
        if (!this.resolveProp(this.project, productCategory.dependencies[i])) {
          return missingDepIndex;
        }
        missingDepIndex++;
      }
    }
    return -1;
  }

  public vGrooveInfo(): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'Why V-Grooves?',
      message: '<p>V-Grooves are a shallow cut across the mat which exposes the core color of the primary mat.' +
        'They give your project an added level of class and elegance.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/v-groove.jpg" alt="Matboard V-Groove"/></p>' +
        '<p>The standard size is typically 1/2" offset from the opening but can be customized if desired.</p>' +
        '<h3>How to Order</h3><p>Ordering is easy! Just click the + button on the preview as indicated below.</p>' +
        '<p class="image"><img src="/assets/images/help/v-groove-how-to.png" alt="Matboard V-Groove How To Order"/></p>',
      yesButtonText: 'Add a V-Groove',
      noButtonText: 'No Thanks'
    };

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.project.vGrooveOffset === 0) {
        this.project.vGrooveOffset = this.project.availableVGrooveOffsets ? this.project.availableVGrooveOffsets.min : environment.vGroove.offset.openingEdgeMinOffset;
      }
    });
  }

  @ViewChild('preview') preview: ProjectPreviewComponent;
  public customCornerInfo(): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'What are Custom Corners',
      message: '<p>Custom corners are a unique way to get more out of your matboard design.  We offer a lot of options ranging from ' +
        'oval or diamond corners.  Check out the list for a full list of offerings.</p>' +
        '<h3>How to Order</h3><p>Ordering is easy! Just click the icon in the top left corner of the opening to make a selection.</p>' +
        '<p class="image"><img src="/assets/images/help/custom-corners-how-to.png" alt="Custom Corners - How To Order"/>' +
        '<img src="/assets/images/help/custom-corners.png" alt="Custom Corners - Options"/></p>',
      yesButtonText: 'See Custom Corners',
      noButtonText: 'No Thanks'
    };

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.preview && this.preview.shapeSelector && this.preview.shapeSelector.select) {
        this.preview.shapeSelector.select.open();
      }
    });
  }

  public designUploaded(design: Design) {
    this.project.design = design;
  }

  public designUploadFailed(err: any) {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'Problem with Uploaded Design',
      yesButtonText: 'OK'
    };
    switch (err.Message) {
      case 'INVALID_DESIGN_FILE': dialogRef.componentInstance.data.message = '<p>An invalid design file was uploaded.</p>'; break;
      case 'MISSING_WCX': dialogRef.componentInstance.data.message = '<p>Please upload any referenced clip art (WCX) file along with the design file.</p>'; break;
      default: dialogRef.componentInstance.data.message = '<p>An unexpected error occurred: <strong>' + err.Message + '</strong></p>'; break;
    }
  }

  public removePrint() {
    if (this.project.print) {
      this.project.print = null;
    } else {
      this.project.openings.forEach( op => {
        op.print = null;
      });
    }
  }

  nextOpeningToPopulate = 0;
  printUploadCompleted(result: any) {
    const print = {
      screenResUrl: result.screenResUrl,
      printResUrl: result.printResUrl,
      isUpload: true
    };

    if (this.project.openings.length === 0) {
      this.project.print = print;
    } else {
      this.project.openings[this.nextOpeningToPopulate].print = print;
      this.nextOpeningToPopulate++;
      if (this.nextOpeningToPopulate >= this.project.openings.length) {
        this.nextOpeningToPopulate = 0;
      }
    }
  }

  printUploadFailed(err: any) {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'Problem with Uploaded Image',
      yesButtonText: 'OK'
    };
    switch (err.Message) {
      case 'INVALID_IMAGE': dialogRef.componentInstance.data.message = '<p>An invalid image file was uploaded.  Please check your image and try again.</p>'; break;
      default: dialogRef.componentInstance.data.message = '<p>An unexpected error occurred: <strong>' + err.Message + '</strong></p>'; break;
    }
  }

  determineTileIcon(productCategory: any): string {
    if (!this.project) return;
    switch (this.determineCategoryState(productCategory)) {
      case 'new': return 'plus';
      case 'entered': return 'pencil';
      case 'required': return 'selection-alert';
      case 'locked': return 'lock';
    }
  }

  determineCategoryState(productCategory: any): string {
    const r = this.project && this.project.restrictions ? this.project.restrictions : null;
    if (productCategory.type === 'SIZE-SELECTOR') {
      if (r && r.outerSizeLocked && r.openingPositionsLocked) {
        return 'locked';
      } else {
        return this.project.size.incomplete ? 'new' : 'entered';
      }
    }

    if (productCategory.projectProperty === 'topMat' && r && r.requiredMats > this.project.numMats && this.project.numMats === 0) {
      return 'required';
    }

    if (productCategory.projectProperty === 'bottomMat' && r && r.requiredMats > this.project.numMats) {
      return 'required';
    }

    if (productCategory.selection.length === 0) {
      return 'new';
    } else {
      return 'entered';
    }
  }

  viewDescriptions = {
    profile: 'Profile',
    corner: 'Corner',
    front: 'Front',
    back: 'Back',
    spec: 'Specifications'
  };

  resetCarousel() {
    this.selectedCarouselTab = 0;
  }

  public get activeShareType(): any {
    return this.shareTypes.filter(s => s.active)[0];
  }

  public enableNativeShare(activeShareType): boolean {
    if (!isPlatformBrowser(this.platformId) || !activeShareType) return false;
    const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(this.window.navigator.userAgent.toLowerCase());
    const isMobile = this.media.isMatched(BREAKPOINTS.Standard.XSmall);
    const canNativeShare = this.window.navigator && this.window.navigator.share ? true : false;
    return (isMobile || isTablet) && canNativeShare;
  }

  public showShareMenu(activeShareType): boolean
  {
    if (!activeShareType) return false;
    switch (activeShareType.name)
    {
      case 'product': return true;
      case 'preview': return !this.enableNativeShare(activeShareType);
      case 'help': return false;
      default: return false;
    }
  }

  public gettingProjectId = false;
  public async startShare(shareType: any) {
    this.shareTypes.forEach(s => s.active = false);
    shareType.active = true;
    this.gettingProjectId = true;
    await this.project.idAsync();
    this.gettingProjectId = false;

    // use native sharing for previews on a phones and tablets
    if (shareType.name === 'preview' && this.enableNativeShare(shareType)) {
      const response = await fetch(shareType.link());
      const blob = await response.blob();
      await this.window.navigator.share( {
        files: [
          new File(
            [blob],
            'preview.jpg',
            {
              type: "image/jpeg",
              lastModified: new Date().getTime()
            }
          )
        ]
      });
      shareType.active = false;
    }

    if (shareType.name === 'help') {
      if (this.chatStatus === 'NOT_INITIALIZED') {
        this.getHelpWithDesignDeferredData = shareType;
        this.chatService.initialize();
      } else {
        this.getHelpWithThisDesign(shareType);
      }
    }
  }

  private getHelpWithThisDesign(shareType) {
    if (this.chatStatus.startsWith('AVAILABLE')) {
      this.chatService.track('Design Link Created', shareType.link());
      this.chatService.startChat();
    } else {
      const dialogRef = this.dialog.open(ContactUsDialogComponent);
      dialogRef.componentInstance.chatLoadFailed = this.chatStatus === 'LOAD_FAILED';
      dialogRef.componentInstance.message.DesignReference = shareType.link();
    }
  }

  public sharePlatforms: any = [
    {
      name: 'pinterest',
      description: 'Pinterest',
      icon: 'pinterest',
      iconColor: '#E60023'
    },
    {
      name: 'facebook',
      description: 'Facebook',
      icon: 'facebook-box',
      iconColor: '#4267B2'
    },
    {
      name: 'x',
      description: 'X (formerly Twitter)',
      icon: 'x-social',
      iconColor: '#000000'
    },
    {
      name: 'whatsapp',
      description: 'WhatsApp',
      icon: 'whatsapp',
      iconColor: '#128C7E'
    },
    {
      name: 'messenger',
      description: 'FB Messenger',
      icon: 'messenger',
      iconColor: '#006AFF'
    },
    {
      name: 'email',
      description: 'Email',
      icon: 'email',
      iconColor: '#918e8e'
    },
    {
      name: 'copy',
      description: 'Copy to Clipboard',
      icon: 'link-plus'
    },
    {
      name: 'downloadFile',
      description: 'Download Design',
      icon: 'download',
      link: () => this.project.shareUrl("aqua", "green-border", true)
    }
  ];

  public shareTypes: any = [
    {
      name: 'product',
      icon: 'share',
      text: 'Share Product',
      toolTip: 'Share your design with a friend',
      link: () => this.shareBaseUrl + this.project.id,
      buttonColor: 'tertiary',
      excludePlatforms: ['downloadFile']
    },
    {
      name: 'preview',
      icon: 'image-share',
      text: 'Share Design',
      toolTip: 'Share design preview image',
      link: () => this.project.shareUrl("aqua", "green-border", false),
      buttonColor: 'tertiary'
    },
    {
      name: 'help',
      icon: 'face-agent',
      text: 'Get Help with this Design ',
      toolTip: 'Get help with your design',
      link: () => this.shareBaseUrl + this.project.id,
      buttonColor: 'secondary'
    }
  ]

  public ngOnDestroy() {
    if (this.project) this.project.destroy();
    if (this.chatSubRef && !this.chatSubRef.closed) this.chatSubRef.unsubscribe();
    if (this.cartChangeSub && !this.cartChangeSub.closed) this.cartChangeSub.unsubscribe();
    if (this.abTestSubRef && !this.abTestSubRef.closed) this.abTestSubRef.unsubscribe();
    if (this.projectChangeSubRef && !this.projectChangeSubRef.closed) this.projectChangeSubRef.unsubscribe();
    if (this.projectSizeChangeSubRef && !this.projectSizeChangeSubRef.closed) this.projectSizeChangeSubRef.unsubscribe();
    if (this.projectSizeChange2SubRef && !this.projectSizeChange2SubRef.closed) this.projectSizeChange2SubRef.unsubscribe();
    this.chatService.destroy();
  }
}
